import React from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { sdk } from '@/lib/sdk'
import { useCommunity } from '@/lib/CommunityContext'
import { useTranslation } from 'react-i18next'
import { type Section } from '@/types/sections'
import { SectionFormFields, type SectionFormValues } from './SectionFormFields'
import { generateSlug, slugErrorFrom, slugProblem } from '@/lib/slug'
import { emptyAccessGrants, toAccessGrantInput, visibilityRank } from '@/types/access'
import type { ApiErrorLike } from '@/lib/apiError'

/** The section as the form holds it. `order` is not edited here, so it is not
    part of the form: the dialog sends back the one the section already has. */
const valuesOf = (section: Section): SectionFormValues => ({
  icon: section.icon || 'home',
  title: section.title,
  slug: section.slug,
  description: section.description || '',
  visibility: section.visibility,
  access: section.access ?? emptyAccessGrants(),
  isHome: section.isHome,
  lockedDisplay: section.lockedDisplay ?? 'section_and_spaces',
  languages: section.languages ?? []
})

interface EditSectionDialogProps {
  section: Section
  open: boolean
  onOpenChange: (open: boolean) => void
  onSectionUpdated?: () => void
}

export const EditSectionDialog = ({
  section,
  open,
  onOpenChange,
  onSectionUpdated
}: EditSectionDialogProps) => {
  const { t } = useTranslation(['common', 'navigation'])
  const { community } = useCommunity()

  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState('')
  const [slugError, setSlugError] = React.useState<string | null>(null)

  const [values, setValues] = React.useState<SectionFormValues>(() => valuesOf(section))

  React.useEffect(() => {
    if (open) {
      setValues(valuesOf(section))
      setError('')
      setSlugError(null)
      setIsLoading(false)
    }
  }, [open, section])

  /* What the server will do anyway, said before it does it. A section that
     closes drags the spaces inside it up to its own level, and finding that out
     afterwards is finding out that something was published wrong. */
  const tightenCount = React.useMemo(() => (section.spaces || []).filter(
    (space) => visibilityRank(space.visibility) < visibilityRank(values.visibility)
  ).length, [section.spaces, values.visibility])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!values.title.trim()) {
      setError(t('common:validation.titleRequired'))
      return
    }

    const slug = generateSlug(values.slug)
    if (slug && slugProblem(slug, 'section')) {
      setSlugError(t('common:sections.slugReserved', { slug }))
      return
    }

    setIsLoading(true)
    setError('')
    setSlugError(null)

    try {
      await sdk.sections.update(section.id, {
        title: values.title.trim(),
        description: values.description.trim() || undefined,
        slug,
        icon: values.icon || 'home',
        order: section.order,
        visibility: values.visibility,
        isHome: values.isHome,
        /* Only when it is private. Sending an empty set alongside another
           visibility would erase grants nobody asked to erase — and somebody
           who flips to Members and back expects to find their list intact. */
        access: values.visibility === 'private' ? toAccessGrantInput(values.access) : undefined,
        lockedDisplay: values.lockedDisplay,
        /* F-252: the home section is for everyone; the API refuses a language on it. */
        languages: values.isHome ? [] : values.languages
      })

      onOpenChange(false)
      onSectionUpdated?.()
    } catch (caught) {
      const err = caught as ApiErrorLike
      const onSlug = slugErrorFrom(err)
      if (onSlug) setSlugError(onSlug)
      else setError(err?.response?.data?.message || t('common:errors.updateFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent guard className="max-w-[540px] max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('navigation:sidebar.editSection')}</DialogTitle>
          <DialogDescription>
            {t('common:sections.editDescription')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg">
              {error}
            </div>
          )}

          <SectionFormFields
            values={values}
            onChange={setValues}
            disabled={isLoading}
            communityId={community?.id}
            tightenCount={tightenCount}
            slugError={slugError}
          />

          <DialogFooter>
            <Button data-dialog-cancel type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
              {t('common:actions.cancel')}
            </Button>
            <Button type="submit" variant="secondary" disabled={isLoading}>
              {isLoading ? t('common:actions.saving') : t('common:actions.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
